import Trans from 'next-translate/Trans';
import useLocale from 'next-translate/useTranslation';

import LandingPageNavigation from '../layout/LandingPageNavigation';
import ButtonLink from '../links/ButtonLink';

export default function AgentsHero() {
  const { t } = useLocale('chatbots');

  return (
    <div className='relative isolate overflow-hidden bg-white dark:bg-slate-900'>
      <LandingPageNavigation />

      {/* dot pattern, faded toward the bottom */}
      <div className='absolute inset-0 -z-10 bg-[radial-gradient(#80808026_1px,transparent_1px)] bg-[size:22px_22px] [mask-image:radial-gradient(ellipse_70%_55%_at_50%_0%,#000_60%,transparent_100%)]'></div>

      {/* single soft brand tint — no rainbow */}
      <div
        className='absolute -top-40 right-[-10%] -z-10 h-[36rem] w-[36rem] rounded-full bg-primary-400/15 blur-3xl dark:bg-primary-500/10'
        aria-hidden='true'
      />

      <div className='mx-auto max-w-7xl px-6 pb-24 pt-10 sm:pb-32 lg:flex lg:items-center lg:gap-x-16 lg:px-8'>
        <div className='mx-auto max-w-2xl lg:mx-0 lg:max-w-2xl lg:flex-shrink-0 lg:pt-8'>
          <div className='mb-8 flex sm:mt-6'>
            <a
              href='#channels'
              className='relative inline-flex items-center gap-x-2 rounded-full px-4 py-1.5 text-sm font-medium leading-6 text-gray-600 ring-1 ring-gray-900/10 transition-all hover:ring-gray-900/30 dark:text-slate-300 dark:ring-white/10 dark:hover:ring-white/30'
            >
              <span className='inline-flex h-2 w-2 animate-pulse rounded-full bg-emerald-500'></span>
              <span>
                <span className='font-semibold text-primary-600 dark:text-primary-400'>
                  {t('New')}
                </span>{' '}
                {t('Your agent now answers phone calls')}
              </span>
            </a>
          </div>

          <h1 className='text-[2.75rem] font-extrabold leading-[1.05] tracking-tight text-gray-900 dark:text-white sm:text-6xl'>
            <Trans
              i18nKey='hero-headline'
              ns='chatbots'
              components={[
                <span
                  key='0'
                  className='font-serif font-medium italic text-primary-600 dark:text-primary-400'
                />,
              ]}
            />
          </h1>

          <p className='mt-6 text-lg leading-8 text-gray-600 dark:text-slate-300'>
            {t(
              'Train an agent on your knowledge base, give it your brand voice, and let it resolve support questions, qualify leads, and book meetings — on your website, WhatsApp, Slack, and over the phone.',
            )}
          </p>

          <div className='mt-10 flex flex-wrap items-center gap-x-6 gap-y-4'>
            <ButtonLink
              href='/login?preset=bots'
              variant='dark'
              className='group relative overflow-hidden rounded-full px-6 py-3 shadow-lg transition-all duration-200 hover:scale-[1.03]'
            >
              <span className='relative flex items-center gap-2'>
                {t('Build your agent free')}
                <svg
                  className='h-4 w-4 transition-transform group-hover:translate-x-1'
                  fill='none'
                  viewBox='0 0 24 24'
                  strokeWidth='2'
                  stroke='currentColor'
                >
                  <path
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    d='M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3'
                  />
                </svg>
              </span>
            </ButtonLink>
            <a
              href='#demo'
              className='text-sm font-semibold leading-6 text-gray-900 transition-colors hover:text-primary-600 dark:text-white dark:hover:text-primary-400'
            >
              {t('See it in action')} <span aria-hidden='true'>→</span>
            </a>
          </div>
          <p className='mt-4 text-sm text-gray-500 dark:text-slate-400'>
            {t('No credit card required')} ·{' '}
            {t('Ready in minutes, no code needed')}
          </p>
        </div>

        {/* One agent, every channel — composed conversation */}
        <div className='relative mx-auto mt-16 w-full max-w-lg sm:mt-24 lg:mt-16'>
          {/* compact live-call strip (mobile only) */}
          <div className='mb-4 flex items-center justify-between rounded-2xl bg-gray-900 px-4 py-3 shadow-xl ring-1 ring-white/10 sm:hidden'>
            <div className='flex items-center gap-2.5'>
              <span className='relative flex h-2.5 w-2.5'>
                <span className='absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-60'></span>
                <span className='relative inline-flex h-2.5 w-2.5 rounded-full bg-emerald-400'></span>
              </span>
              <span className='text-sm font-semibold text-white'>
                {t('Live call')} · +1 (334) 689-6674
              </span>
            </div>
            <span className='text-xs font-medium text-emerald-400'>00:42</span>
          </div>
          {/* chat card */}
          <div className='relative z-10 mr-10 rounded-3xl bg-white shadow-2xl shadow-gray-900/10 ring-1 ring-gray-900/10 dark:bg-slate-800 dark:ring-white/10 sm:mr-16'>
            <div className='flex items-center gap-3 border-b border-gray-100 px-6 py-4 dark:border-white/5'>
              <div className='flex h-9 w-9 items-center justify-center rounded-full bg-primary-600 text-sm font-bold text-white'>
                A
              </div>
              <div>
                <p className='text-sm font-semibold text-gray-900 dark:text-white'>
                  {t('Your AI agent')}
                </p>
                <p className='flex items-center gap-1.5 text-xs text-gray-500 dark:text-slate-400'>
                  <span className='inline-flex h-1.5 w-1.5 rounded-full bg-emerald-500'></span>
                  {t('Online — replies instantly')}
                </p>
              </div>
            </div>
            <div className='space-y-4 px-6 py-6'>
              <div className='flex justify-end'>
                <p className='max-w-[75%] rounded-2xl rounded-br-md bg-gray-100 px-4 py-2.5 text-sm leading-relaxed text-gray-900 dark:bg-slate-700 dark:text-slate-100'>
                  {t('My order arrived damaged. Can you help?')}
                </p>
              </div>
              <div className='flex items-end gap-2'>
                <div className='flex h-6 w-6 flex-none items-center justify-center rounded-full bg-primary-600 text-[10px] font-bold text-white'>
                  A
                </div>
                <p className='max-w-[80%] rounded-2xl rounded-bl-md bg-primary-600 px-4 py-2.5 text-sm leading-relaxed text-white'>
                  {t(
                    "So sorry about that! I've arranged a free replacement — it arrives Thursday.",
                  )}
                </p>
              </div>
              <div className='flex justify-end'>
                <p className='max-w-[75%] rounded-2xl rounded-br-md bg-gray-100 px-4 py-2.5 text-sm leading-relaxed text-gray-900 dark:bg-slate-700 dark:text-slate-100'>
                  {t('That was fast — thank you!')}
                </p>
              </div>
            </div>
          </div>

          {/* voice call card */}
          <div className='absolute -top-24 right-0 z-20 hidden w-60 rounded-2xl bg-gray-900 p-4 shadow-2xl ring-1 ring-white/10 sm:-right-2 sm:block'>
            <div className='flex items-center justify-between'>
              <p className='text-xs font-medium uppercase tracking-wider text-gray-400'>
                {t('Live call')}
              </p>
              <span className='flex items-center gap-1.5 text-xs font-medium text-emerald-400'>
                <span className='inline-flex h-1.5 w-1.5 animate-pulse rounded-full bg-emerald-400'></span>
                00:42
              </span>
            </div>
            <p className='mt-2 text-sm font-semibold text-white'>
              +1 (334) 689-6674
            </p>
            <div
              className='mt-3 flex h-8 items-end gap-[3px]'
              aria-hidden='true'
            >
              {[
                5, 12, 20, 28, 18, 30, 24, 14, 26, 32, 22, 10, 18, 27, 15, 8,
                21, 29, 12, 6,
              ].map((h, i) => (
                <span
                  key={i}
                  className='w-[4px] rounded-full bg-primary-400/90'
                  style={{
                    height: `${h}px`,
                    animation: `agent-wave 1.1s ease-in-out ${i * 0.07}s infinite alternate`,
                  }}
                ></span>
              ))}
            </div>
            <p className='mt-3 text-xs leading-relaxed text-gray-400'>
              {t('“Hi, this is your AI agent. How can I help?”')}
            </p>
          </div>

          {/* channel chips */}
          <div className='absolute -bottom-8 -left-4 z-20 flex items-center gap-2 rounded-full bg-white py-2 pl-3 pr-4 shadow-xl ring-1 ring-gray-900/10 dark:bg-slate-800 dark:ring-white/10 sm:-left-8'>
            <span className='flex h-7 w-7 items-center justify-center rounded-full bg-[#25D366]/10 text-[#25D366]'>
              <svg viewBox='0 0 24 24' fill='currentColor' className='h-4 w-4'>
                <path d='M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z' />
              </svg>
            </span>
            <span className='flex h-7 w-7 items-center justify-center rounded-full bg-[#4A154B]/10 text-[#4A154B] dark:bg-white/10 dark:text-white'>
              <svg viewBox='0 0 24 24' fill='currentColor' className='h-4 w-4'>
                <path d='M5.042 15.165a2.528 2.528 0 01-2.52 2.523A2.528 2.528 0 010 15.165a2.527 2.527 0 012.522-2.52h2.52v2.52zM6.313 15.165a2.527 2.527 0 012.521-2.52 2.527 2.527 0 012.521 2.52v6.313A2.528 2.528 0 018.834 24a2.528 2.528 0 01-2.521-2.522v-6.313zM8.834 5.042a2.528 2.528 0 01-2.521-2.52A2.528 2.528 0 018.834 0a2.528 2.528 0 012.521 2.522v2.52H8.834zM8.834 6.313a2.528 2.528 0 012.521 2.521 2.528 2.528 0 01-2.521 2.521H2.522A2.528 2.528 0 010 8.834a2.528 2.528 0 012.522-2.521h6.312zM18.956 8.834a2.528 2.528 0 012.522-2.521A2.528 2.528 0 0124 8.834a2.528 2.528 0 01-2.522 2.521h-2.522V8.834zM17.688 8.834a2.528 2.528 0 01-2.523 2.521 2.527 2.527 0 01-2.52-2.521V2.522A2.527 2.527 0 0115.165 0a2.528 2.528 0 012.523 2.522v6.312zM15.165 18.956a2.528 2.528 0 012.523 2.522A2.528 2.528 0 0115.165 24a2.527 2.527 0 01-2.52-2.522v-2.522h2.52zM15.165 17.688a2.527 2.527 0 01-2.52-2.523 2.526 2.526 0 012.52-2.52h6.313A2.527 2.527 0 0124 15.165a2.528 2.528 0 01-2.522 2.523h-6.313z' />
              </svg>
            </span>
            <span className='flex h-7 w-7 items-center justify-center rounded-full bg-primary-600/10 text-primary-600 dark:text-primary-400'>
              <svg
                viewBox='0 0 24 24'
                fill='none'
                stroke='currentColor'
                strokeWidth='2'
                className='h-4 w-4'
              >
                <path
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  d='M2.25 6.75c0 8.284 6.716 15 15 15h2.25a2.25 2.25 0 002.25-2.25v-1.372c0-.516-.351-.966-.852-1.091l-4.423-1.106c-.44-.11-.902.055-1.173.417l-.97 1.293c-.282.376-.769.542-1.21.38a12.035 12.035 0 01-7.143-7.143c-.162-.441.004-.928.38-1.21l1.293-.97c.363-.271.527-.734.417-1.173L6.963 3.102a1.125 1.125 0 00-1.091-.852H4.5A2.25 2.25 0 002.25 4.5v2.25z'
                />
              </svg>
            </span>
            <span className='text-xs font-medium text-gray-600 dark:text-slate-300'>
              {t('+ 9 channels')}
            </span>
          </div>
        </div>
      </div>

      <style jsx global>{`
        @keyframes agent-wave {
          from {
            transform: scaleY(0.35);
          }
          to {
            transform: scaleY(1);
          }
        }
        @media (prefers-reduced-motion: reduce) {
          [style*='agent-wave'] {
            animation: none !important;
          }
        }
      `}</style>
    </div>
  );
}
